import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useDashboardData } from "../../context/DashboardContext";
import { WIDGET_REGISTRY } from "./widgets";
import type { WidgetConfig, LayoutItem } from "./types";
import {
  formatMetricLabel,
  type CustomMetricFormat,
  type CustomMetricOperation,
  type CustomMetricVariant,
  type MetricColorTheme,
} from "./customMetricUtils";

interface WidgetConfigDialogProps {
  widgetType: string;
  widget?: WidgetConfig | null;
  layoutItem?: LayoutItem | null;
  onSave: (widget: WidgetConfig, size?: { w: number; h: number }) => void;
  onClose: () => void;
}

const SCORECARD_VARIANTS: Array<{ value: CustomMetricVariant; label: string; description: string; size: { w: number; h: number } }> = [
  { value: "metric", label: "Hero Card", description: "Large headline number with icon and color.", size: { w: 4, h: 4 } },
  { value: "stat", label: "Compact Stat", description: "Small KPI chip for dense dashboards.", size: { w: 3, h: 2 } },
  { value: "additional", label: "Detailed Stat", description: "Large stat with optional supporting note.", size: { w: 4, h: 4 } },
];

const SCORECARD_FORMATS: Array<{ value: CustomMetricFormat; label: string }> = [
  { value: "currency", label: "Currency" },
  { value: "currencyAbbreviated", label: "Currency Abbreviated" },
  { value: "percent", label: "Percent" },
  { value: "number", label: "Number" },
];

const SCORECARD_OPERATIONS: Array<{ value: CustomMetricOperation; label: string; description: string }> = [
  { value: "direct", label: "Single metric", description: "Show one field exactly as-is." },
  { value: "sum", label: "A + B", description: "Combine two metrics into a total." },
  { value: "difference", label: "A - B", description: "Subtract one metric from another." },
  { value: "ratio", label: "A / B", description: "Compute a ratio between two metrics." },
  { value: "percentage", label: "(A / B) * 100", description: "Turn a ratio into a percentage." },
];

const METRIC_ICON_OPTIONS = [
  { value: "dollar", label: "Dollar" },
  { value: "trending", label: "Trending" },
  { value: "target", label: "Target" },
  { value: "chart", label: "Chart" },
  { value: "users", label: "Users" },
  { value: "wallet", label: "Wallet" },
  { value: "percent", label: "Percent" },
];

const METRIC_COLOR_OPTIONS: Array<{ value: MetricColorTheme; label: string }> = [
  { value: "brand", label: "Brand" },
  { value: "green", label: "Green" },
  { value: "blue", label: "Blue" },
  { value: "purple", label: "Purple" },
];

const CHART_Y_FORMAT_OPTIONS = [
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "percent", label: "Percent" },
];

function FieldShell({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-2">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        {hint ? <div className="text-xs text-muted-foreground mt-0.5">{hint}</div> : null}
      </div>
      {children}
    </label>
  );
}

function InputClassName() {
  return "w-full rounded-xl border border-border bg-background/70 px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/15";
}

function getWidgetMode(widgetType: string): "scorecard" | "chart" | "table" | "commission" {
  if (widgetType === "table") return "table";
  if (widgetType === "commission") return "commission";
  if (widgetType === "metric" || widgetType === "stat" || widgetType === "additionalStat" || widgetType === "customMetric") {
    return "scorecard";
  }
  return "chart";
}

export function WidgetConfigDialog({
  widgetType,
  widget,
  layoutItem,
  onSave,
  onClose,
}: WidgetConfigDialogProps) {
  const { metrics, chartData, tableData } = useDashboardData();
  const isEditing = Boolean(widget);
  const mode = getWidgetMode(widgetType);
  const inputClassName = InputClassName();
  const currentConfig = widget?.config ?? {};

  const metricOptions = useMemo(
    () => Object.keys(metrics).map((key) => ({ value: key, label: formatMetricLabel(key) })).sort((a, b) => a.label.localeCompare(b.label)),
    [metrics],
  );
  const chartDataOptions = useMemo(
    () => Object.keys(chartData)
      .filter((key) => Array.isArray(chartData[key]) && chartData[key].length > 0)
      .map((key) => ({ value: key, label: formatMetricLabel(key) })),
    [chartData],
  );
  const tableDataOptions = useMemo(
    () => Object.keys(tableData)
      .filter((key) => Array.isArray(tableData[key]))
      .map((key) => ({ value: key, label: formatMetricLabel(key) })),
    [tableData],
  );

  const [title, setTitle] = useState(widget?.title ?? (widgetType === "metric" ? "Custom Metric" : widgetType === "stat" ? "Custom Stat" : widgetType === "additionalStat" ? "Detailed KPI" : "New Widget"));

  const [scorecardVariant, setScorecardVariant] = useState<CustomMetricVariant>(
    (currentConfig.variant as CustomMetricVariant)
      ?? (widgetType === "stat" ? "stat" : widgetType === "additionalStat" ? "additional" : "metric"),
  );
  const [scorecardMetricA, setScorecardMetricA] = useState<string>(
    (currentConfig.metricKeyA as string)
      ?? (currentConfig.metricKey as string)
      ?? metricOptions[0]?.value
      ?? "",
  );
  const [scorecardMetricB, setScorecardMetricB] = useState<string>((currentConfig.metricKeyB as string) ?? metricOptions[1]?.value ?? metricOptions[0]?.value ?? "");
  const [scorecardOperation, setScorecardOperation] = useState<CustomMetricOperation>((currentConfig.operation as CustomMetricOperation) ?? "direct");
  const [scorecardFormat, setScorecardFormat] = useState<CustomMetricFormat>(
    (currentConfig.format as CustomMetricFormat) ?? (scorecardVariant === "metric" ? "currencyAbbreviated" : "number"),
  );
  const [scorecardIcon, setScorecardIcon] = useState<string>((currentConfig.icon as string) ?? "dollar");
  const [scorecardColor, setScorecardColor] = useState<MetricColorTheme>((currentConfig.color as MetricColorTheme) ?? "brand");
  const [scorecardDescription, setScorecardDescription] = useState<string>((currentConfig.description as string) ?? "");

  const [chartDataKey, setChartDataKey] = useState<string>((currentConfig.dataKey as string) ?? chartDataOptions[0]?.value ?? "");
  const [chartValueKey, setChartValueKey] = useState<string>((currentConfig.valueKey as string) ?? "value");
  const [chartXKey, setChartXKey] = useState<string>((currentConfig.xKey as string) ?? "name");
  const [chartYFormat, setChartYFormat] = useState<string>((currentConfig.yFormat as string) ?? "number");
  const [chartTooltipFormat, setChartTooltipFormat] = useState<string>((currentConfig.tooltipFormat as string) ?? "number");
  const [chartShowGrid, setChartShowGrid] = useState<boolean>(Boolean(currentConfig.showGrid));
  const [tableDataKey, setTableDataKey] = useState<string>((currentConfig.dataKey as string) ?? tableDataOptions[0]?.value ?? "");
  const [tableRowKey, setTableRowKey] = useState<string>((currentConfig.rowKey as string) ?? "id");

  const [commissionCashKey, setCommissionCashKey] = useState<string>((currentConfig.cashKey as string) ?? metricOptions[0]?.value ?? "");
  const [commissionRate, setCommissionRate] = useState<string>(String((currentConfig.rate as number | undefined) ?? 0.1));
  const [commissionColor, setCommissionColor] = useState<string>((currentConfig.colorTheme as string) ?? "indigo");
  const [commissionDescription, setCommissionDescription] = useState<string>((currentConfig.description as string) ?? "");

  const currentChartRows = useMemo(
    () => (mode === "chart" ? (((chartData[chartDataKey] ?? []) as Record<string, unknown>[] | undefined) ?? []) : []),
    [chartData, chartDataKey, mode],
  );
  const chartFieldOptions = useMemo(() => {
    const sample = currentChartRows.find((row) => row && typeof row === "object");
    if (!sample) return [];
    return Object.keys(sample).map((key) => ({ value: key, label: formatMetricLabel(key) }));
  }, [currentChartRows]);

  useEffect(() => {
    if (!chartDataKey && chartDataOptions[0]) {
      setChartDataKey(chartDataOptions[0].value);
    }
  }, [chartDataKey, chartDataOptions]);

  useEffect(() => {
    if (mode !== "chart") return;
    const rows = (chartData[chartDataKey] as Record<string, unknown>[] | undefined) ?? [];
    const sample = rows.find((row) => row && typeof row === "object");
    if (!sample) return;

    const fields = Object.keys(sample);
    if (!fields.includes(chartValueKey)) {
      const numericField = fields.find((field) => typeof sample[field] === "number") ?? fields[1] ?? fields[0];
      if (numericField) setChartValueKey(numericField);
    }
    if (!fields.includes(chartXKey)) {
      const xField = fields.find((field) => typeof sample[field] === "string") ?? fields[0];
      if (xField) setChartXKey(xField);
    }
  }, [chartData, chartDataKey, chartValueKey, chartXKey, mode]);

  const save = () => {
    if (mode === "scorecard") {
      const nextWidget: WidgetConfig = {
        id: widget?.id ?? `customMetric-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: "customMetric",
        title: title.trim() || "Custom Metric",
        config: {
          variant: scorecardVariant,
          metricKeyA: scorecardMetricA,
          metricKeyB: scorecardOperation === "direct" ? undefined : scorecardMetricB,
          operation: scorecardOperation,
          format: scorecardFormat,
          icon: scorecardVariant === "metric" ? scorecardIcon : undefined,
          color: scorecardVariant === "metric" ? scorecardColor : undefined,
          description: scorecardVariant === "additional" ? scorecardDescription.trim() : undefined,
        },
      };

      const targetSize = layoutItem
        ? { w: layoutItem.w, h: layoutItem.h }
        : SCORECARD_VARIANTS.find((variant) => variant.value === scorecardVariant)?.size;
      onSave(nextWidget, targetSize);
      return;
    }

    if (mode === "chart") {
      const nextWidget: WidgetConfig = {
        id: widget?.id ?? `${widgetType}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: widgetType,
        title: title.trim() || "Custom Chart",
        config: {
          dataKey: chartDataKey,
          valueKey: widgetType === "pieChart" ? undefined : chartValueKey,
          xKey: widgetType === "pieChart" ? undefined : chartXKey,
          yFormat: widgetType === "areaChart" ? chartYFormat : undefined,
          tooltipFormat: widgetType === "areaChart" ? chartTooltipFormat : undefined,
          showGrid: widgetType !== "pieChart" ? chartShowGrid : undefined,
        },
      };
      onSave(nextWidget, layoutItem ? { w: layoutItem.w, h: layoutItem.h } : WIDGET_REGISTRY[widgetType]?.defaultSize);
      return;
    }

    if (mode === "table") {
      const nextWidget: WidgetConfig = {
        id: widget?.id ?? `table-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: "table",
        title: title.trim() || "Data Table",
        config: {
          dataKey: tableDataKey,
          rowKey: tableRowKey.trim() || "id",
          title: title.trim() || "Data Table",
        },
      };
      onSave(nextWidget, layoutItem ? { w: layoutItem.w, h: layoutItem.h } : (WIDGET_REGISTRY.table?.defaultSize ?? { w: 12, h: 8 }));
      return;
    }

    const parsedRate = Number(commissionRate);
    const nextWidget: WidgetConfig = {
      id: widget?.id ?? `commission-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "commission",
      title: title.trim() || "Commission",
      config: {
        cashKey: commissionCashKey,
        rate: Number.isFinite(parsedRate) ? parsedRate : 0.1,
        colorTheme: commissionColor,
        description: commissionDescription.trim() || undefined,
      },
    };
    onSave(nextWidget, layoutItem ? { w: layoutItem.w, h: layoutItem.h } : (WIDGET_REGISTRY.commission?.defaultSize ?? { w: 12, h: 4 }));
  };

  const saveDisabled = (() => {
    if (mode === "scorecard") {
      return !title.trim() || !scorecardMetricA || (scorecardOperation !== "direct" && !scorecardMetricB);
    }
    if (mode === "chart") return !title.trim() || !chartDataKey;
    if (mode === "table") return !title.trim() || !tableDataKey;
    return !title.trim() || !commissionCashKey;
  })();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="w-full max-w-3xl max-h-[88vh] overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-border bg-gradient-to-r from-[var(--brand-primary)]/10 via-transparent to-transparent px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">Widget Builder</div>
              <h2 className="mt-2 text-xl font-semibold text-foreground">
                {isEditing ? "Configure Widget" : "Create Widget"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {mode === "scorecard" && "Build a scorecard from the live metrics available on this page."}
                {mode === "chart" && "Choose one of the chart datasets already available on this dashboard."}
                {mode === "table" && "Select which table dataset this widget should render."}
                {mode === "commission" && "Configure how this commission card calculates and presents its payout."}
              </p>
            </div>
            <button onClick={onClose} className="rounded-xl border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted">
              Close
            </button>
          </div>
        </div>

        <div className="max-h-[calc(88vh-164px)] overflow-y-auto px-6 py-6">
          <div className="grid gap-6 md:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <FieldShell label="Title" hint="This is the label users will see on the widget.">
                <input value={title} onChange={(event) => setTitle(event.target.value)} className={inputClassName} placeholder="Give this widget a useful name" />
              </FieldShell>

              {mode === "scorecard" && (
                <>
                  <FieldShell label="Card Style" hint="Choose how prominent this scorecard should be in the layout.">
                    <div className="grid gap-3 md:grid-cols-3">
                      {SCORECARD_VARIANTS.map((variant) => (
                        <button
                          key={variant.value}
                          type="button"
                          onClick={() => setScorecardVariant(variant.value)}
                          className={`rounded-2xl border p-4 text-left transition-all ${
                            scorecardVariant === variant.value
                              ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/8 shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
                              : "border-border hover:border-[var(--brand-primary)]/30 hover:bg-muted/40"
                          }`}
                        >
                          <div className="text-sm font-semibold text-foreground">{variant.label}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{variant.description}</div>
                          <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{variant.size.w} x {variant.size.h}</div>
                        </button>
                      ))}
                    </div>
                  </FieldShell>

                  <FieldShell label="Calculation" hint="Pick a direct field or combine two fields with a simple operation.">
                    <div className="grid gap-2">
                      {SCORECARD_OPERATIONS.map((operation) => (
                        <button
                          key={operation.value}
                          type="button"
                          onClick={() => setScorecardOperation(operation.value)}
                          className={`rounded-xl border px-4 py-3 text-left transition-all ${
                            scorecardOperation === operation.value
                              ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/8"
                              : "border-border hover:border-[var(--brand-primary)]/30 hover:bg-muted/40"
                          }`}
                        >
                          <div className="text-sm font-medium text-foreground">{operation.label}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{operation.description}</div>
                        </button>
                      ))}
                    </div>
                  </FieldShell>

                  <div className="grid gap-5 md:grid-cols-2">
                    <FieldShell label="Primary Metric" hint="The first field in the calculation.">
                      <select value={scorecardMetricA} onChange={(event) => setScorecardMetricA(event.target.value)} className={inputClassName}>
                        {metricOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </FieldShell>

                    {scorecardOperation !== "direct" && (
                      <FieldShell label="Secondary Metric" hint="Used when your calculation needs a second field.">
                        <select value={scorecardMetricB} onChange={(event) => setScorecardMetricB(event.target.value)} className={inputClassName}>
                          {metricOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </FieldShell>
                    )}
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <FieldShell label="Display Format">
                      <select value={scorecardFormat} onChange={(event) => setScorecardFormat(event.target.value as CustomMetricFormat)} className={inputClassName}>
                        {SCORECARD_FORMATS.map((format) => (
                          <option key={format.value} value={format.value}>{format.label}</option>
                        ))}
                      </select>
                    </FieldShell>

                    {scorecardVariant === "metric" && (
                      <FieldShell label="Color Theme">
                        <select value={scorecardColor} onChange={(event) => setScorecardColor(event.target.value as MetricColorTheme)} className={inputClassName}>
                          {METRIC_COLOR_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </FieldShell>
                    )}
                  </div>

                  {scorecardVariant === "metric" && (
                    <FieldShell label="Icon">
                      <select value={scorecardIcon} onChange={(event) => setScorecardIcon(event.target.value)} className={inputClassName}>
                        {METRIC_ICON_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </FieldShell>
                  )}

                  {scorecardVariant === "additional" && (
                    <FieldShell label="Supporting Note" hint="Optional helper text shown when the card has enough room.">
                      <textarea
                        value={scorecardDescription}
                        onChange={(event) => setScorecardDescription(event.target.value)}
                        className={`${inputClassName} min-h-[96px] resize-none`}
                        placeholder="Explain what this calculation means"
                      />
                    </FieldShell>
                  )}
                </>
              )}

              {mode === "chart" && (
                <>
                  <FieldShell label="Dataset" hint="Pick one of the chart datasets already loaded on this page.">
                    <select value={chartDataKey} onChange={(event) => setChartDataKey(event.target.value)} className={inputClassName}>
                      {chartDataOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </FieldShell>

                  {widgetType !== "pieChart" && (
                    <div className="grid gap-5 md:grid-cols-2">
                      <FieldShell label="X Axis Field">
                        <select value={chartXKey} onChange={(event) => setChartXKey(event.target.value)} className={inputClassName}>
                          {chartFieldOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </FieldShell>
                      <FieldShell label="Value Field">
                        <select value={chartValueKey} onChange={(event) => setChartValueKey(event.target.value)} className={inputClassName}>
                          {chartFieldOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </FieldShell>
                    </div>
                  )}

                  {widgetType === "areaChart" && (
                    <div className="grid gap-5 md:grid-cols-2">
                      <FieldShell label="Y Axis Format">
                        <select value={chartYFormat} onChange={(event) => setChartYFormat(event.target.value)} className={inputClassName}>
                          {CHART_Y_FORMAT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </FieldShell>
                      <FieldShell label="Tooltip Format">
                        <select value={chartTooltipFormat} onChange={(event) => setChartTooltipFormat(event.target.value)} className={inputClassName}>
                          {CHART_Y_FORMAT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </FieldShell>
                    </div>
                  )}

                  {widgetType !== "pieChart" && (
                    <button
                      type="button"
                      onClick={() => setChartShowGrid((value) => !value)}
                      className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition-all ${
                        chartShowGrid ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/8" : "border-border hover:border-[var(--brand-primary)]/30 hover:bg-muted/30"
                      }`}
                    >
                      <div>
                        <div className="text-sm font-medium text-foreground">Show Grid</div>
                        <div className="text-xs text-muted-foreground mt-1">Helpful for charts that need more reading precision.</div>
                      </div>
                      <div className={`h-6 w-11 rounded-full p-1 transition-colors ${chartShowGrid ? "bg-[var(--brand-primary)]" : "bg-muted"}`}>
                        <div className={`h-4 w-4 rounded-full bg-white transition-transform ${chartShowGrid ? "translate-x-5" : ""}`} />
                      </div>
                    </button>
                  )}
                </>
              )}

              {mode === "table" && (
                <>
                  <FieldShell label="Dataset" hint="Pick which dataset this table should render.">
                    <select value={tableDataKey} onChange={(event) => setTableDataKey(event.target.value)} className={inputClassName}>
                      {tableDataOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </FieldShell>
                  <FieldShell label="Row Key" hint="Used to keep rows stable. Leave as `id` unless your table uses another unique field.">
                    <input value={tableRowKey} onChange={(event) => setTableRowKey(event.target.value)} className={inputClassName} placeholder="id" />
                  </FieldShell>
                </>
              )}

              {mode === "commission" && (
                <>
                  <FieldShell label="Cash Metric">
                    <select value={commissionCashKey} onChange={(event) => setCommissionCashKey(event.target.value)} className={inputClassName}>
                      {metricOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </FieldShell>
                  <div className="grid gap-5 md:grid-cols-2">
                    <FieldShell label="Rate" hint="Use decimals, so `0.1` means 10%.">
                      <input value={commissionRate} onChange={(event) => setCommissionRate(event.target.value)} className={inputClassName} />
                    </FieldShell>
                    <FieldShell label="Theme">
                      <select value={commissionColor} onChange={(event) => setCommissionColor(event.target.value)} className={inputClassName}>
                        <option value="indigo">Indigo</option>
                        <option value="emerald">Emerald</option>
                      </select>
                    </FieldShell>
                  </div>
                  <FieldShell label="Supporting Note">
                    <input value={commissionDescription} onChange={(event) => setCommissionDescription(event.target.value)} className={inputClassName} placeholder="Optional helper text" />
                  </FieldShell>
                </>
              )}
            </div>

            <div className="space-y-5">
              <div className="rounded-[1.5rem] border border-border bg-background/60 p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Preview Summary</div>
                <div className="mt-4 space-y-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Type</div>
                    <div className="mt-1 font-medium text-foreground">{mode === "scorecard" ? "Custom Scorecard" : formatMetricLabel(widgetType)}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Title</div>
                    <div className="mt-1 font-medium text-foreground">{title || "Untitled widget"}</div>
                  </div>
                  {mode === "scorecard" && (
                    <>
                      <div>
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Formula</div>
                        <div className="mt-1 font-medium text-foreground">
                          {formatMetricLabel(scorecardMetricA || "metric")}
                          {scorecardOperation !== "direct" ? ` ${scorecardOperation === "sum" ? "+" : scorecardOperation === "difference" ? "-" : scorecardOperation === "ratio" ? "/" : "% of "} ${formatMetricLabel(scorecardMetricB || "metric")}` : ""}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Style</div>
                        <div className="mt-1 font-medium text-foreground">
                          {SCORECARD_VARIANTS.find((variant) => variant.value === scorecardVariant)?.label}
                        </div>
                      </div>
                    </>
                  )}
                  {mode === "chart" && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Dataset</div>
                      <div className="mt-1 font-medium text-foreground">{formatMetricLabel(chartDataKey || "data")}</div>
                    </div>
                  )}
                  {mode === "table" && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Dataset</div>
                      <div className="mt-1 font-medium text-foreground">{formatMetricLabel(tableDataKey || "table")}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-border bg-gradient-to-br from-[var(--brand-primary)]/10 via-transparent to-transparent p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">What Happens Next</div>
                <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                  <li>The widget will be added to the bottom of the current layout.</li>
                  <li>You can drag and resize it after it appears.</li>
                  <li>Save the layout or preset after configuring it so the setup sticks.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
          <div className="text-xs text-muted-foreground">
            {layoutItem ? `Editing current size: ${layoutItem.w} x ${layoutItem.h}` : "New widgets use a sensible default size and can be resized later."}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saveDisabled}
              className="rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isEditing ? "Update Widget" : "Add Widget"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
